// ./src/pages/private/EventMessageComposer/SendLimitNotice.tsx
//
// A refusal that is NOT a failure — the send was declined by a rule, and there is something
// the host can do about it.
//
// ⚠ WHY THIS EXISTS AS ITS OWN THING. Everything the composer could not do used to land in
// one `ErrorAlert` titled "Something went wrong". For a genuine fault that is right; for a
// BURST CAP it is three kinds of wrong at once:
//
//   1. Nothing went wrong. The system worked exactly as designed and said so.
//   2. It is temporary and self-service — the cap disappears the moment the account is
//      verified — and a red "error" gives a host no reason to think so.
//   3. It is the single best moment in the product to complete Stripe Connect, because the
//      host wants something RIGHT NOW that verifying gives them. Spending that moment on
//      "Something went wrong" wastes it.
//
// ⚠ THE SENTENCE IS THE SERVER'S, WORD FOR WORD. It already carries the numbers (`used` of
// `cap`), the reason and the way out, and it is the only side that knows them. Re-writing it
// here would give a host two different accounts of one refusal depending on which layer
// produced it — the same drift `check-entitlement` exists to prevent for plan gates.
//
// ⚠ HOW THE TWO REFUSALS ARE TOLD APART: by the ERROR KEY, which the server already
// distinguishes and has all along. `planFeatureGate` keys its refusal `plan`; `burstCap` keys
// its own `burstCap` (and both carry LIMIT_REACHED). The roadmap recorded this as blocked on
// "making the burst refusal distinguishable — today both come back keyed `plan`"; that was
// checked on 2026-09-10 and was not true. Nothing on the server needed changing for this.
//
// ⚠ NEVER show the plan sentence for a burst refusal. "Your plan allows X per week" is untrue
// — the cap is not a plan term, it lifts on verification — and telling a paying host their
// plan is smaller than they bought is how a refund request starts.
import React from "react";
import { Alert, Button, Group, Stack, Text } from "@mantine/core";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { getFieldError } from "@em/api";

import { AppIcon } from "@/components/generic";
import { getRoutePath } from "@/config";
import { useFeatureGate } from "@/hooks/useFeatureGate";

export type SendRefusalKind = "burst" | "plan" | "allowance" | "other";

export interface SendRefusal {
  kind: SendRefusalKind;
  /** The server's own sentence. Empty for `other` — that path renders `ErrorAlert` instead. */
  message: string;
}

/**
 * Which kind of refusal this is, read off the error KEY rather than the message text.
 *
 * ⚠ Keyed, never matched on wording. A substring check against the copy would break the
 * moment either sentence is edited, and both sentences are copy we expect to tune.
 */
export const classifySendRefusal = (error: OpResult | null | undefined): SendRefusal => {
  if (!error) {
    return { kind: "other", message: "" };
  }

  const burst = getFieldError(error, "burstCap");

  if (burst) {
    return { kind: "burst", message: burst };
  }

  const allowance = getFieldError(error, "allowance");

  if (allowance) {
    // ⚠ ONE KEY, TWO MEANINGS — separated by the CODE, and conflating them would sell
    // somebody something they may not need. `reserveSendAllowance` keys both of these
    // `allowance`: a real shortfall (LIMIT_REACHED — "top up to send") and a FAILED balance
    // LOOKUP ("we could not check your balance just now, nothing was sent"). Only the first
    // is fixed by buying credits; offering a purchase for the second would take money to
    // solve a problem the host does not have.
    if (error.code === OP_RESULT_CODES.LIMIT_REACHED) {
      return { kind: "allowance", message: allowance };
    }

    return { kind: "other", message: "" };
  }

  const plan = getFieldError(error, "plan");

  if (plan) {
    return { kind: "plan", message: plan };
  }

  return { kind: "other", message: "" };
};

export interface SendLimitNoticeProps {
  refusal: SendRefusal;
  /** Opens the top-up panel. Only meaningful for an `allowance` refusal. */
  onTopUp?: () => void;
}

export const SendLimitNotice: React.FC<SendLimitNoticeProps> = ({ refusal, onTopUp }) => {
  const navigate = useNavigate();
  // #60 — a plan refusal goes where `goToUpgrade` decides: on a partylabz event, THIS event's plans.
  const { goToUpgrade, upgradesEvent } = useFeatureGate();
  // ⚠ Translated chrome around an UNTRANSLATED sentence, deliberately. The message is the
  // server's and only the server knows the numbers in it; running it through i18n would mean
  // rewriting it here, which is the drift this component's header forbids. Matches its
  // sibling `SendCostSummary`, which is also `t()` with defaults in this same folder.
  const { t } = useTranslation(["events", "common"]);

  if (refusal.kind === "other") {
    return null;
  }

  const isBurst = refusal.kind === "burst";
  const isAllowance = refusal.kind === "allowance";

  return (
    <Alert
      // ⚠ Yellow, not red. This is a limit with a door out of it, and red says "broken".
      color="yellow"
      variant="light"
      radius="md"
      icon={<AppIcon iconName={isBurst ? "ShieldCheck" : isAllowance ? "Wallet" : "Crown"} size={18} />}
      title={
        isBurst
          ? t("events:composer.limit.burstTitle", {
              defaultValue: "This send is over your current limit",
            })
          : isAllowance
            ? t("events:composer.limit.allowanceTitle", {
                defaultValue: "Not enough left to send this",
              })
            : t("events:composer.limit.planTitle", {
                defaultValue: "Your plan does not include this",
              })
      }
    >
      <Stack gap="sm">
        <Text size="md">{refusal.message}</Text>

        <Group gap="sm" wrap="wrap">
          {isBurst ? (
            <Button
              variant="filled"
              leftSection={<AppIcon iconName="CreditCard" size={14} />}
              onClick={() => navigate(getRoutePath("stripeConnect"))}
            >
              {t("events:composer.limit.connectStripe", { defaultValue: "Connect Stripe" })}
            </Button>
          ) : isAllowance ? (
            <Button
              variant="filled"
              leftSection={<AppIcon iconName="CreditCard" size={14} />}
              onClick={onTopUp}
            >
              {t("events:composer.limit.topUp", { defaultValue: "Buy more" })}
            </Button>
          ) : (
            <Button
              variant="filled"
              leftSection={<AppIcon iconName="Crown" size={14} />}
              onClick={goToUpgrade}
            >
              {upgradesEvent
                ? t("events:composer.limit.upgradeEvent", { defaultValue: "Upgrade this event" })
                : t("events:composer.limit.viewPlans", { defaultValue: "View plans" })}
            </Button>
          )}
        </Group>

        {isAllowance && (
          <Text size="xs" c="dimmed">
            {/* ⚠ A refused batch is ALL-OR-NOTHING (MONETIZATION §4.2) — nothing went out and
                nothing was charged. Say it: a host who thinks half the list was messaged will
                either double-send or not send at all. */}
            {t("events:composer.limit.allowanceWhy", {
              defaultValue:
                "Nothing was sent and nothing was charged — sends are all-or-nothing. Top up and send again, and your bundle is used before your monthly allowance.",
            })}
          </Text>
        )}

        {isBurst && (
          <Text size="xs" c="dimmed">
            {/* ⚠ Says why the limit exists. A host who thinks they are being throttled
                arbitrarily reads it as a downgrade; knowing it is an anti-abuse speed limit
                on UNVERIFIED accounts makes verifying feel like the fix that it is. */}
            {t("events:composer.limit.burstWhy", {
              defaultValue:
                "New accounts have a weekly send limit until they are verified. Connecting Stripe removes it straight away — it is the same connection you need to sell tickets.",
            })}
          </Text>
        )}
      </Stack>
    </Alert>
  );
};
